import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FilterChips, Pagination, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import type { EstadoPortabilidade, Portabilidade } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { useFiltros } from "../../lib/use-filtros";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { fmtDataHora } from "../../lib/refinanciamento";
import {
  ESTADOS_PORTABILIDADE,
  ESTADO_PORTABILIDADE_OPERADOR,
  acoesDoBancoNaPortabilidade,
  pillDaPortabilidade,
  prazoDaEtapa,
} from "../../lib/portabilidade";
import {
  AbrirPortabilidadeModal,
  CancelarPortabilidadeModal,
  DecisaoCredoraModal,
  InformarCondicoesModal,
  InformarPagamentoModal,
  LiquidarPortabilidadeModal,
  PortabilidadeDetalheModal,
} from "../../components/PortabilidadeModais";

type Papel = "" | "proponente" | "credora";
type Acao = { tipo: "proposta" | "pagamento" | "liquidar" | "cancelar" | "aceitar" | "recusar"; p: Portabilidade } | null;

/**
 * Portabilidade do lado do banco. O mesmo banco aparece nos dois papeis: como
 * PROPONENTE (abre, informa o pagamento, cancela) e como CREDORA (informa o
 * saldo, liquida). A fila mostra os dois; o filtro de papel separa.
 */
export function BancoPortabilidade() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["banco", "portabilidade"],
    queryFn: () => atlas.portabilidade.doBanco(),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });
  const [estado, setEstado] = useState<"" | EstadoPortabilidade>("");
  const [papel, setPapel] = useState<Papel>("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [acao, setAcao] = useState<Acao>(null);
  const [abrir, setAbrir] = useState(false);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const base = useMemo(() => (q.data?.itens ?? []).map((p) => ({ ...p, origemAdf: p.origem.adf })), [q.data]);
  type Linha = (typeof base)[number];
  const termo = busca.trim();
  const filtros = useMemo(
    () => ({
      estado: estado ? (p: Linha) => p.estado === estado : null,
      papel: papel ? (p: Linha) => p.papel === papel : null,
      busca: termo ? (p: Linha) => matchAnyKeys(p, termo, ["id", "servidorNome", "matricula", "cpfMasked", "origemAdf", "adfDestino"]) : null,
    }),
    [estado, papel, termo],
  );
  const linhas = useMemo(() => aplicarFiltros(base, filtros), [base, filtros]);
  const semEstado = useMemo(() => aplicarFiltros(base, filtros, "estado"), [base, filtros]);
  const contEstado = useMemo(() => contagens(semEstado, (p) => p.estado), [semEstado]);
  const semPapel = useMemo(() => aplicarFiltros(base, filtros, "papel"), [base, filtros]);
  const contPapel = useMemo(() => contagens(semPapel, (p) => p.papel ?? ""), [semPapel]);
  const suaVez = base.filter((p) => {
    const a = acoesDoBancoNaPortabilidade(p);
    return a.proposta || a.aceitar || a.pagamento || a.liquidar;
  }).length;

  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "estado", ligado: estado !== "", limpar: () => setEstado("") },
      { nome: "papel", ligado: papel !== "", limpar: () => setPapel("") },
    ],
    setPagina,
  );
  const { filtrar } = filtrosUi;
  const daPagina = recortar(linhas, pagina, porPagina);

  const feito = (p: Portabilidade, texto: string) => {
    setAcao(null);
    setAbrir(false);
    setAviso(texto.replace("{id}", p.id).replace("{destino}", p.adfDestino ?? ""));
    void qc.invalidateQueries({ queryKey: ["banco", "portabilidade"] });
    void qc.invalidateQueries({ queryKey: ["banco", "contratos-api"] });
  };

  const columns: Column<Linha>[] = [
    {
      key: "estado",
      header: "Etapa",
      render: (p) => (
        <>
          <Pill variant={pillDaPortabilidade(p.estado)}>{p.analiseManual && p.estado === "aguardando_liquidacao" ? "Análise da averbadora" : ESTADO_PORTABILIDADE_OPERADOR[p.estado]}</Pill>
          {prazoDaEtapa(p.expiraEm) ? <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>{prazoDaEtapa(p.expiraEm)}</div> : null}
        </>
      ),
    },
    {
      key: "id",
      header: "Portabilidade",
      mono: true,
      render: (p) => (
        <button type="button" onClick={() => setDetalhe(p.id)} style={{ all: "unset", cursor: "pointer", textDecoration: "underline" }}>
          {p.id}
        </button>
      ),
    },
    { key: "papel", header: "Seu papel", render: (p) => (p.papel === "proponente" ? "Proponente (destino)" : "Credora (origem)") },
    {
      key: "servidor",
      header: "Servidor",
      render: (p) => (
        <>
          <div>{p.servidorNome}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{p.cpfMasked} / {p.matricula}</div>
        </>
      ),
    },
    {
      key: "origem",
      header: "Origem",
      render: (p) => (
        <>
          <div style={{ fontFamily: "var(--font-mono)" }}>{p.origem.adf}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{p.papel === "credora" ? `para ${p.proponente.nome}` : `de ${p.credora.nome}`}</div>
        </>
      ),
    },
    { key: "parcela", header: "Parcela atual", align: "right", render: (p) => fmtBRL(p.origem.parcela) },
    { key: "saldo", header: "Saldo informado", align: "right", render: (p) => (p.saldoInformado == null ? "—" : fmtBRL(p.saldoInformado)) },
    {
      key: "destino",
      header: "Destino",
      render: (p) => (p.condicoes ? `${p.condicoes.prazo}x de ${fmtBRL(p.condicoes.valorParcela)}${p.adfDestino ? ` · ${p.adfDestino}` : ""}` : "—"),
    },
    { key: "criadoEm", header: "Aberta em", render: (p) => fmtDataHora(p.criadoEm) },
    {
      key: "acoes",
      header: "",
      render: (p) => {
        const a = acoesDoBancoNaPortabilidade(p);
        return (
          <div style={{ display: "flex", gap: 6 }}>
            {a.proposta ? <Button size="sm" onClick={() => setAcao({ tipo: "proposta", p })}>{p.fluxoLegado ? "Completar dados do contrato" : "Apresentar proposta"}</Button> : null}
            {a.aceitar ? <Button size="sm" onClick={() => setAcao({ tipo: "aceitar", p })}>Aceitar e informar saldo</Button> : null}
            {a.recusar ? <Button size="sm" variant="ghost" onClick={() => setAcao({ tipo: "recusar", p })}>Recusar</Button> : null}
            {a.pagamento ? <Button size="sm" onClick={() => setAcao({ tipo: "pagamento", p })}>Informar pagamento</Button> : null}
            {a.liquidar ? <Button size="sm" onClick={() => setAcao({ tipo: "liquidar", p })}>Liquidar</Button> : null}
            {a.cancelar ? <Button size="sm" variant="ghost" onClick={() => setAcao({ tipo: "cancelar", p })}>{p.solicitadoPor === "servidor" && p.estado === "aguardando_proposta" ? "Recusar pedido" : "Cancelar"}</Button> : null}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Propostas
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Portabilidade</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 760 }}>
            Como <b>proponente</b>, você traz para a sua consignatária um contrato que o servidor tem em outro credor: apresenta as condições, o servidor aceita a proposta, a
            credora informa o saldo e você informa o valor pago e pode anexar o comprovante. Se o servidor iniciou o pedido, apresente a proposta primeiro. Como <b>credora</b>, você informa o saldo e liquida. Na liquidação o
            contrato antigo é quitado e o novo nasce averbado. A credora aceita informando o saldo na mesma ação ou recusa com motivo. A consignatária de destino pode recusar o pedido do servidor antes de apresentar a proposta. Credora inativa: anexe o comprovante para análise da averbadora. Etapas vencidas sob sua responsabilidade bloqueiam novas averbações até a regularização.
          </p>
        </div>
        <Button onClick={() => setAbrir(true)}>Nova portabilidade</Button>
      </header>

      {aviso ? <div role="status" style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{aviso}</div> : null}
      {suaVez > 0 ? (
        <div style={{ fontSize: 13, color: "var(--gold-500)", fontWeight: 600 }}>
          {suaVez} portabilidade{suaVez === 1 ? "" : "s"} aguardando a sua ação.
        </div>
      ) : null}

      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por portabilidade, servidor, CPF, matrícula ou ADF"
        {...filtrosUi.propsFilterBar}
        actions={
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <FilterChips
              variant="segmentado"
              label="Papel"
              value={papel}
              onChange={(v) => filtrar(setPapel)(v as Papel)}
              options={[
                { value: "", label: "Todos", count: semPapel.length },
                { value: "proponente", label: "Proponente", count: contPapel.proponente ?? 0 },
                { value: "credora", label: "Credora", count: contPapel.credora ?? 0 },
              ]}
            />
            <FilterChips
              variant="segmentado"
              label="Etapa"
              value={estado}
              onChange={(v) => filtrar(setEstado)(v as "" | EstadoPortabilidade)}
              options={[
                { value: "", label: "Todas", count: semEstado.length },
                ...ESTADOS_PORTABILIDADE.map((e) => ({ value: e, label: ESTADO_PORTABILIDADE_OPERADOR[e], count: contEstado[e] ?? 0 })),
              ]}
            />
          </div>
        }
      />

      {q.isError ? (
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13 }}>
          {mensagemDeErro(q.error, "Não foi possível carregar as portabilidades.")}
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(p) => p.id}
        emptyState={
          <EmptyState
            motivo={motivoVazio(base.length, linhas.length)}
            filtroVazio="Nenhuma portabilidade com esses filtros."
            semDados="Nenhuma portabilidade ainda. Para trazer um contrato de outra consignatária, use Nova portabilidade."
            onLimpar={filtrosUi.limparFiltros}
          />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="portabilidades"
      />

      {abrir ? (
        <AbrirPortabilidadeModal
          onClose={() => setAbrir(false)}
          onFeito={(p) => feito(p, "Portabilidade {id} aberta. Ela segue para a credora quando o servidor confirmar no app.")}
        />
      ) : null}
      {acao?.tipo === "proposta" ? <InformarCondicoesModal p={acao.p} onClose={() => setAcao(null)} onFeito={(p) => feito(p, p.fluxoLegado ? "Dados do contrato da portabilidade {id} completados. Registre o valor pago quando o pagamento for realizado." : "Proposta da portabilidade {id} enviada para aceite do servidor.")} /> : null}
      {acao?.tipo === "pagamento" ? (
        <InformarPagamentoModal p={acao.p} onClose={() => setAcao(null)} onFeito={(p) => feito(p, "Pagamento da portabilidade {id} informado. Aguarda a liquidação pela credora.")} />
      ) : null}
      {acao?.tipo === "liquidar" ? (
        <LiquidarPortabilidadeModal p={acao.p} onClose={() => setAcao(null)} onFeito={(p) => feito(p, "Portabilidade {id} liquidada: contrato quitado, destino {destino} averbado.")} />
      ) : null}
      {acao?.tipo === "cancelar" ? (
        <CancelarPortabilidadeModal p={acao.p} quem="banco" onClose={() => setAcao(null)} onFeito={(p) => feito(p, "Portabilidade {id} cancelada.")} />
      ) : null}
      {acao?.tipo === "aceitar" || acao?.tipo === "recusar" ? <DecisaoCredoraModal p={acao.p} decisao={acao.tipo} onClose={() => setAcao(null)} onFeito={(p) => feito(p, "Decisão da credora registrada na portabilidade {id}.")} /> : null}
      {detalhe ? <PortabilidadeDetalheModal id={detalhe} carregar={atlas.portabilidade.doBancoDetalhe} onClose={() => setDetalhe(null)} /> : null}
    </div>
  );
}
